import {
  BaSeActionItem,
  BaSeButton,
  BaSeHeader,
  BaSeIcon,
  BaSeSmall1,
  BaSeTheme
} from '@base/react';

import * as Sebrae from '@sebrae/react-core';
import * as React from 'react';
import { useUserController } from '../../../../../hooks/controllers/user-controller';
import { useModule } from '../../../../../hooks/module/module';
import { ContentContext } from '../../../context/content-context';
import { managerLegacyPaths } from '../../../routes';

import { useGeneralSearch } from '../../../pages/candidates/hook/useGeneralSearch';
import { useMenu } from '../../../pages/candidates/hook/useMenu';
import {
  BoxLocalization,
  ContainerHeader,
  CustomButtonsComponentsWrapper,
  CustomComponentsWrapper,
  HeaderItem,
  HeaderItems,
  HeaderLogoContainer,
  HeaderTitle,
  HeaderWrapper,
  LineHeader,
  LogoSebrae,
} from './header-styled';

const cache = Sebrae.StorageUtil.inTemporaryStore();
const cacheKeySebrae = '@empretec:sebrae';
const cacheKeyPagination = '@empretec:paginationBoard';

// eslint-disable-next-line sonarjs/cognitive-complexity
const CustomHeaderComponent = () => {
  const { isAuthenticated, hasRole, hasRoles } = Sebrae.useAuth();
  const { navigateTo } = Sebrae.useSebraeRouter();
  const { findSebrae } = useUserController();

  const { openDialogBuscaGeral } = useMenu();
  const { handleCpfSearched, fetchDataInterview } = useGeneralSearch();

  React.useEffect(() => {
    if (!cache.hasItem(cacheKeySebrae) && isAuthenticated) {
      findSebrae().then((sebrae) => {
        cache.setItem(cacheKeySebrae, sebrae);
      });
    }
  }, [findSebrae, isAuthenticated]);

  const { setShowContentManageClass, setShowManageRegional } =
    React.useContext(ContentContext);
  return (
    <CustomComponentsWrapper>
      {isAuthenticated && hasRoles(['ADMINISTRADOR', 'GESTOR']) && (
        <>
          <CustomButtonsComponentsWrapper>
            <BaSeButton
              onClick={() => {
                setShowContentManageClass(false);
                setShowManageRegional(false);

                navigateTo(managerLegacyPaths.entrevista, { keepQueryString: true });
                cache.removeItem('@empretec:listPaginationColumns');
              }}
              color={BaSeTheme.colors.institucionais.cinzaSebrae75}
              value={'Página Inicial'}
              type="tertiary"
            />

            <BaSeButton
              onClick={() => {
                setShowContentManageClass(true);
                cache.setItem(cacheKeyPagination, {
                  pageIndex: 0,
                  pageSize: 10,
                });
              }}
              color={BaSeTheme.colors.institucionais.cinzaSebrae75}
              value={'Gerenciar Turma'}
              type="tertiary"
            />
            {hasRoles(['GESTOR_SEBRAE', 'ADMINISTRADOR']) && (
              <BaSeButton
                onClick={() => {
                  setShowManageRegional(true);
                  setShowContentManageClass(false);
                  cache.setItem(cacheKeyPagination, {
                    pageIndex: 0,
                    pageSize: 10,
                  });
                }}
                color={BaSeTheme.colors.institucionais.cinzaSebrae75}
                value={'Gerenciar Regional'}
                type="tertiary"
              />
            )}

            <BaSeButton
              onClick={() => {
                openDialogBuscaGeral(handleCpfSearched, fetchDataInterview);
              }}
              color={BaSeTheme.colors.institucionais.cinzaSebrae75}
              value={'Pesquisa Geral'}
              type="tertiary"
            />
          </CustomButtonsComponentsWrapper>
          <HeaderItems>
            {hasRole('ADMINISTRADOR') && (
              <HeaderItem>
                <BaSeIcon
                  color={BaSeTheme.colors.institucionais.cinzaSebrae75}
                  description="Calibrar notas"
                  name="setting"
                  size={25}
                  onClick={() =>
                    navigateTo('/gestor/configuracoes', {
                      keepQueryString: true,
                    })
                  }
                />
              </HeaderItem>
            )}
            <HeaderItem>
              <BaSeIcon
                color={BaSeTheme.colors.institucionais.cinzaSebrae75}
                description="Amei"
                name="users-alt"
                size={25}
                onClick={() =>
                  navigateTo('/gestor/amei', {
                    keepQueryString: true,
                  })
                }
              />
            </HeaderItem>
          </HeaderItems>
        </>
      )}
    </CustomComponentsWrapper>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const LayoutHeader = () => {
  const cache = Sebrae.StorageUtil.inTemporaryStore();
  const cacheKeySebrae = '@empretec:sebrae';

  const getSebrae = React.useCallback(() => {
    return cache.hasItem(cacheKeySebrae) && ` ${cache.getItem(cacheKeySebrae)}`;
  }, [cache]);

  const { keycloak, isAuthenticated } = Sebrae.useAuth();
  const { navigateTo } = Sebrae.useSebraeRouter();
  const { findSebrae } = useUserController();
  const {
    modules,
    moduleSelected,
    loading: moduleLoading,
    error: moduleError,
  } = useModule();

  React.useEffect(() => {
    if (!cache.hasItem(cacheKeySebrae) && isAuthenticated) {
      findSebrae().then((sebrae) => {
        cache.setItem(cacheKeySebrae, sebrae);
      });
    }
  }, [cache, findSebrae, isAuthenticated]);

  const systemLogo = () => (
    <HeaderLogoContainer>
      <LogoSebrae />

      {isAuthenticated &&
        (moduleError ? (
          <BoxLocalization>
            <BaSeSmall1
              color={BaSeTheme.colors.feedbackInterface.erro}
              isBold={true}
            >
              {moduleError}
            </BaSeSmall1>
          </BoxLocalization>
        ) : (
          (moduleLoading || modules.length > 1) && (
            <div style={{ width: 600 }}>
              <BaSeActionItem
                type="tertiary"
                color={BaSeTheme.colors.defaultColors.white}
                buttonType="base-button"
                position="top-right"
                closeOnAction={true}
                customButtonProps={{
                  isLoading: moduleLoading,
                  value:
                    (
                      <HeaderTitle>
                        Gestor {getSebrae()} {moduleSelected?.name}
                      </HeaderTitle>
                    ) ?? '-',
                  color: BaSeTheme.colors.institucionais.cinzaSebrae60,
                  rightIcon: 'angle-down',
                  descriptionRightIcon: 'Selecione o módulo desejado',
                }}
                items={modules
                  .filter((i) => i.id !== moduleSelected?.id)
                  .map((i) => ({
                    ...i,
                    icon: null,
                    text: 'Gestor ' + getSebrae() + ' ' + i.name,
                    isSelected: i.name === moduleSelected?.name,
                    callback: () =>
                      navigateTo(`${managerLegacyPaths.entrevista}?modulo=${i.id}`, {
                        forceReload: true,
                      }),
                  }))}
              />
            </div>
          )
        ))}

      {/* {moduleError ? (
        <BoxLocalization>
          <BaSeSmall1
            color={BaSeTheme.colors.feedbackInterface.erro}
            isBold={true}
          >
            {moduleError}
          </BaSeSmall1>
        </BoxLocalization>
      ) : (
        (moduleLoading || modules.length > 1) && (
          <div style={{ width: 600 }}>
            <BaSeActionItem
              type="tertiary"
              color={BaSeTheme.colors.defaultColors.white}
              buttonType="base-button"
              position="top-right"
              closeOnAction={true}
              customButtonProps={{
                isLoading: moduleLoading,
                value:
                  (
                    <HeaderTitle>
                      Gestor {getSebrae()} {moduleSelected?.name}
                    </HeaderTitle>
                  ) ?? '-',
                color: BaSeTheme.colors.institucionais.cinzaSebrae60,
                rightIcon: 'angle-down',
                descriptionRightIcon: 'Selecione o módulo desejado',
              }}
              items={modules
                .filter((i) => i.id !== moduleSelected?.id)
                .map((i) => ({
                  ...i,
                  icon: null,
                  text: 'Gestor ' + getSebrae() + i.name,
                  isSelected: i.name === moduleSelected?.name,
                  callback: () =>
                    navigateTo(`${managerLegacyPaths.entrevista}?modulo=${i.id}`, {
                      forceReload: true,
                    }),
                }))}
            />
          </div>
        )
      )} */}
    </HeaderLogoContainer>
  );

  return (
    <ContainerHeader
      gradientColor={moduleSelected?.settingsMap?.primaryColor}
      isAuthenticated={isAuthenticated}
    >
      <HeaderWrapper>
        <BaSeHeader
          backgroundColor={BaSeTheme.colors.defaultColors.transparent}
          sidebarBackgroundColor={BaSeTheme.colors.defaultColors.transparent}
          sidebarTextColor={BaSeTheme.colors.institucionais.cinzaSebrae75}
          textColor={BaSeTheme.colors.institucionais.cinzaSebrae75}
          collapsedBreakpoint={
            isAuthenticated ? 'onDesktopLarge' : 'onMobileLarge'
          }
          delimitedOnBreakpoint="desktop"
          systemName="Gestor Empretec"
          systemLogo={systemLogo}
          accountProfileProps={{ keycloak }}

          customComponents={[
            {
              showOnExpanded: true,
              showOnCollapsed: true,
              showOnSidebar: true,
              component: () => <CustomHeaderComponent />,
            },
          ]}
          logoOnClick={() => console.log('Logo do Sebrae')}
        />

        {/*    <BaSeAccountProfile
          authRealm="externo"
          authenticated
          backgroundColor={BaSeTheme.colors.defaultColors.transparent}
          foregroundColor="#ffffff"
          accountProfileProps={{ keycloak }}
          keycloak={keycloak}

        /> */}
        <LineHeader />

      </HeaderWrapper>
    </ContainerHeader>
  );
};
